package fr.manyverse.app.presentation.error

import fr.manyverse.app.domain.error.AuthenticationError
import fr.manyverse.app.domain.error.NetworkError
import fr.manyverse.app.domain.error.ValidationError
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.error_invalid_email
import manyverse.shared.generated.resources.error_network
import manyverse.shared.generated.resources.error_oauth_failed
import manyverse.shared.generated.resources.error_otp_invalid
import manyverse.shared.generated.resources.error_otp_rate_limit
import manyverse.shared.generated.resources.error_session_expired
import manyverse.shared.generated.resources.error_unknown_auth
import org.jetbrains.compose.resources.StringResource

/**
 * Mappe les erreurs auth (Supabase + domaine) vers des `StringResource` localisées.
 * Le matching se fait sur des motifs robustes du message brut Supabase, indépendants
 * de la langue serveur.
 */
object AuthErrorMapper {

    /**
     * Message d'un échec de connexion par navigateur (Google, Apple). Les motifs de
     * [toStringResource] visent la saisie du code OTP : un refus OAuth y passerait pour un
     * « code invalide », que l'utilisateur n'a pourtant jamais saisi.
     */
    fun toOAuthStringResource(error: Throwable): StringResource =
        if (error is NetworkError) Res.string.error_network else Res.string.error_oauth_failed

    fun toStringResource(error: Throwable): StringResource {
        if (error is ValidationError) {
            val msg = error.message?.lowercase().orEmpty()
            return when {
                "email" in msg -> Res.string.error_invalid_email
                "otp" in msg || "code" in msg -> Res.string.error_otp_invalid
                else -> Res.string.error_unknown_auth
            }
        }

        if (error is NetworkError) return Res.string.error_network

        val msg = error.message?.lowercase().orEmpty()

        return when {
            "rate limit" in msg || "rate_limit" in msg || "too many" in msg ->
                Res.string.error_otp_rate_limit

            "expired" in msg && ("token" in msg || "otp" in msg || "code" in msg) ->
                Res.string.error_otp_invalid

            ("invalid" in msg || "incorrect" in msg) &&
                    ("token" in msg || "otp" in msg || "code" in msg) ->
                Res.string.error_otp_invalid

            "network" in msg || "unreachable" in msg || "timeout" in msg ||
                    "connect" in msg || "no internet" in msg ->
                Res.string.error_network

            "session" in msg && "expir" in msg ->
                Res.string.error_session_expired

            "jwt" in msg && ("expir" in msg || "invalid" in msg) ->
                Res.string.error_session_expired

            error is AuthenticationError -> Res.string.error_oauth_failed

            else -> Res.string.error_unknown_auth
        }
    }
}
